/** The phone's error boundary (C27): a renderer that throws — a screen, a mapper, an adapter,
 *  in the page's tree or inside the frame's own root (`DeviceFrame` lifts those here) — shows
 *  an honest state in the phone's place instead of taking the page down: "The preview couldn't
 *  render this", that the record and the draft are untouched, and **Retry** (draws the phone
 *  again). A new render (`resetKey`) clears it too. The crash goes to Sentry, as the app's
 *  boundary sends its own. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

export const PREVIEW_CRASHED = 'The preview couldn’t render this.';
export const PREVIEW_CRASHED_DETAIL = 'The record and your draft are untouched.';

interface Props {
  /** A new value (a new render) clears a crash. */
  resetKey: unknown;
  children: ReactNode;
}

interface State {
  error: Error | null;
  /** The `resetKey` the crash happened under. */
  key: unknown;
  /** Bumped by Retry: the phone's tree mounts again. */
  attempt: number;
}

export class PreviewErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, key: undefined, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // A crash belongs to the render it happened in: another render starts clean.
    if (state.error && props.resetKey !== state.key) return { error: null, key: props.resetKey };
    if (!state.error && props.resetKey !== state.key) return { key: props.resetKey };
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, { tags: { area: 'mobile-preview' }, extra: { componentStack: info.componentStack } });
  }

  private retry = () => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));

  override render(): ReactNode {
    if (!this.state.error) return <Keyed key={this.state.attempt}>{this.props.children}</Keyed>;
    return (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon fontSize="inherit" />}
        data-preview-state="crashed"
        action={
          <Button color="inherit" size="small" onClick={this.retry}>
            Retry
          </Button>
        }
      >
        {PREVIEW_CRASHED} {PREVIEW_CRASHED_DETAIL}
      </Alert>
    );
  }
}

/** A keyed wrapper: Retry remounts the phone (a new frame and root). */
function Keyed({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
